import { FC, useEffect, useRef, useState } from 'react';
import ProductListComponent from '../../components/product-list/ProductListComponent';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/AppReducer';
import { ApolloActions } from '../../redux/ApolloActions';
import { mapFiltersToQuery } from '../../components/filter/generic-filter/model';
import { GET_PRODUCTS } from '../../graphql/queries/products';
import {
	FilterQueryVM,
	FilterType,
	IFiltersUrlParameters,
	ISuggestion,
	ITrackingEvent,
	TrackingEmailVariables,
	TrackingEventStatus,
	TrackingEventType
} from '../../common/view-model';
import { useCallback } from 'react';
import { AppActions } from '../../redux/AppActions';
import { useSetUrlParameters } from '../../hooks/useSetUrlParameters';
import { SearchActions } from '../../redux/search/SearchActions';
import { FilterActions } from '../../redux/FilterActions';
import { useGetUrlParameters } from '../../hooks/useGetUrlParameters';
import { GET_BRAND } from '../../graphql/queries/brand';
import { useLocation, useSearchParams } from 'react-router';
import { getFilterTypeKeyForUrl } from '../../helpers/urlParametersHelpers';
import { getUrlTrackingParamsObject } from '../../helpers/getUrlParamsObject';
import { handleTracking } from '../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { IirisIdentityKey, getLocalStorageValue } from '../../helpers/authenticationHelper';
import { useAppDispatch } from '../../redux/store';

const ProductsPage: FC = () => {
	const appState = useSelector(({ appState: state }: RootState) => state);

	const hasMappedAllFilters = useSelector(
		({ appState }: RootState) => !!appState.filterState?.hasMappedAllFilters
	);

	const dispatch = useAppDispatch();

	const getProducts = (variables: any = null) =>
		dispatch(ApolloActions.query({ query: GET_PRODUCTS, variables }));

	const setStandAloneLayout = (standAloneLayout: boolean) =>
		dispatch(AppActions.setStandAloneLayout(standAloneLayout));

	const setProfileLayout = (isProfileLayout: boolean) =>
		dispatch(AppActions.setProfileLayout(isProfileLayout));

	const getBrandById = (variables: any = null) =>
		dispatch(ApolloActions.query({ query: GET_BRAND, variables }));

	const setProductsCached = (products: any) => dispatch(AppActions.setProductsCached(products));

	const setPage = (page: number) => dispatch(AppActions.setPage(page));

	const setUserReturns = (state: boolean) => dispatch(AppActions.setUserReturns(state));

	const setSearchTerm = (searchTerm: ISuggestion) =>
		dispatch(SearchActions.setSearchTerm(searchTerm));

	const setSelectedFilters = (selectedFilters: IFiltersUrlParameters) =>
		dispatch(FilterActions.setSelectedFiltersFromUrl(selectedFilters));

	const setFilterUrl = (url: string) =>
		dispatch({ type: FilterActions.MAP_BROWSER_URL, payload: url });

	const mobileFilterPanelRef = useRef<HTMLDivElement>(null);
	const [viewMode] = useState('grid');
	const [products, setProducts] = useState<any>([]);
	const [totalCount, setTotalCount] = useState<number>();
	const [initLoading, setInitLoading] = useState(true);
	const [isStartGetProduct, setIsStartGetProduct] = useState(false);

	const [offset, setOffset] = useState(0);
	const limit = 24;
	const [isInfiniteLoading, setIsInfiniteLoading] = useState(false);
	const { storeState } = appState;
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const brandId = params.get(getFilterTypeKeyForUrl(FilterType.BRAND));
	const [isBrandLoaded, setIsBrandLoaded] = useState(
		!!((brandId && storeState?.brand) || !brandId)
	);
	const [ingredientsTemplateId, setIngredientsTemplateId] = useState<number | undefined>(
		appState?.userState?.ingredientsTemplateId
	);
	const selectedFilters = useSelector(
		(state: RootState) => state.appState.filterState?.selectedFilters
	);
	const searchTerm = useSelector((state: RootState) => state.appState.searchState?.searchTerm);
	const isLoading = useSelector((state: RootState) => state.appState.isLoadingData);
	const expoEvent = useSelector((state: RootState) => state.appState.storeState?.getCurrentEvent);
	const selectedSortBy = useSelector(
		(state: RootState) => state.appState.filterState?.sortBySelected
	);
	const eventParticipationEventIdMap: Record<string, string> = {
		'expo-west-26': '1085',
		'newtopia-26': '1112'
	};
	const [searchParams, setSearchParams] = useSearchParams();
	const isLoadingCallback = useCallback(
		() => dispatch(AppActions.setIsLoadingData(true)),
		[dispatch]
	);

	const extractEventIds = (filters: any[] | undefined): string[] | undefined => {
		if (!filters) return undefined;
		const eventIds = filters
			.filter(f => f.type === FilterType.SHOW_PARTICIPATION)
			.map(f => eventParticipationEventIdMap[f.id || ''])
			.filter((id): id is string => !!id);
		return eventIds.length ? eventIds : undefined;
	};

	const calculateFilterFlags = (filters: any[] | undefined) => {
		if (!filters) {
			return {
				whatsNew: false,
				isNewBeacon: false,
				isNewProduct: false,
				isNextyFinalist: false
			};
		}
		return {
			whatsNew:
				filters.some(f => f.type === FilterType.WHATS_NEW) &&
				!filters.some(f => f.type === FilterType.NEW_TO_BEACON) &&
				!filters.some(f => f.type === FilterType.NEW_TO_MARKET) &&
				!filters.some(f => f.type === FilterType.NEXTY_FINALISTS),
			isNewBeacon: filters.some(f => f.type === FilterType.NEW_TO_BEACON),
			isNewProduct: filters.some(f => f.type === FilterType.NEW_TO_MARKET),
			isNextyFinalist: filters.some(f => f.type === FilterType.NEXTY_FINALISTS)
		};
	};

	const isBrandValid = (queryFilters: FilterQueryVM[], currentBrandId: string | null): boolean => {
		if (currentBrandId === null) return true;
		const brandObject = queryFilters.find(item => item.key === 'Brand');
		return !brandObject || brandObject.ids.includes(currentBrandId);
	};

	useEffect(() => {
		setIngredientsTemplateId(appState?.userState?.ingredientsTemplateId);
	}, [appState?.userState]);

	useEffect(() => {
		if (mobileFilterPanelRef.current || expoEvent === undefined) return;
		if (!isInfiniteLoading && !storeState?.userReturns) {
			setPage(0);
			setOffset(0);
			isLoadingCallback();
		}
		if (storeState?.userReturns) return;

		let variable: FilterQueryVM[] = [];
		if (selectedFilters && selectedFilters.length > 0) {
			variable = mapFiltersToQuery(selectedFilters);
			if (!isBrandValid(variable, brandId)) return;
		}

		const eventIds = extractEventIds(selectedFilters);
		const filterFlags = calculateFilterFlags(selectedFilters);

		setIsStartGetProduct(true);
		getProducts({
			genericFilters: variable,
			sortAttribute: selectedSortBy?.productSort.attribute,
			sortDirection: selectedSortBy?.productSort.order,
			templateId: String(ingredientsTemplateId || '1'),
			pagination: { offset: offset, limit: limit },
			searchTerm: { type: searchTerm?.type || null, term: searchTerm?.name },
			expoEventId: expoEvent?.id || null,
			eventIds: eventIds,
			whatsNew: filterFlags.whatsNew,
			isNewBeacon: filterFlags.isNewBeacon,
			isNewProduct: filterFlags.isNewProduct,
			isNextyFinalist: filterFlags.isNextyFinalist
		});
	}, [selectedFilters, selectedSortBy, offset, searchTerm, mobileFilterPanelRef.current]);

	useEffect(() => {
		if (isStartGetProduct && !isLoading) {
			setInitLoading(false);
		}
	}, [isStartGetProduct, isLoading]);

	useEffect(() => {
		if (isInfiniteLoading) {
			setProducts([...products, ...(appState?.storeState?.products || [])]);
			setIsInfiniteLoading(false);
		} else setProducts(appState?.storeState?.products);
		setTotalCount(appState?.storeState?.totalCount);
	}, [appState?.storeState?.products]);

	useEffect(() => {
		setProfileLayout(false);
		setStandAloneLayout(false);
		let searchTrackingObject = getUrlTrackingParamsObject(
			TrackingEmailVariables.UTM_TRACKING,
			searchParams
		);
		if (searchTrackingObject.trackingParam && searchTrackingObject.trackingParam === 'true') {
			const trackingEvent: ITrackingEvent = {
				transactionId: uuidv4(),
				status: TrackingEventStatus.SUCCESS,
				type: TrackingEventType.EMAIL_TRACKING_DISCOVERY,
				timeStamp: new Date().toISOString(),
				entityId: getLocalStorageValue(IirisIdentityKey.USER_ID) || '',
				eventPayload: {
					id: `email-tracking-discovery-${new Date().toISOString()}`,
					name: 'email-tracking-discovery',
					type: 'email-tracking',
					url: window.location.href,
					tags: [],
					metadata: {
						user_id: appState?.userState?.user?.user_id,
						user_type: appState?.userState?.user_type,
						enviroment: import.meta.env.VITE_ENV_NAME
					}
				}
			};
			handleTracking(trackingEvent);
			delete searchTrackingObject.object[TrackingEmailVariables.UTM_TRACKING];
			setSearchParams(searchTrackingObject.object);
		}
	}, []);

	useEffect(() => {
		setUserReturns(false);
		const savedPosition = parseInt('' + sessionStorage.getItem('scrollPosition'), 10);
		if (storeState?.userReturns || savedPosition > 0) {
			setInitLoading(false);
			return;
		}
		setOffset(0);
		setPage(0);
	}, [selectedFilters, selectedSortBy]);

	useEffect(() => {
		const savedPosition = parseInt('' + sessionStorage.getItem('scrollPosition'), 10);
		if (savedPosition > 0) return;
		if (!storeState?.userReturns) {
			setProductsCached([
				...(storeState?.productsCached || []),
				...(appState?.storeState?.products || [])
			]);
		}
		if (storeState?.page === 0) {
			setProductsCached([...(appState?.storeState?.products || [])]);
		}
		setTotalCount(appState?.storeState?.totalCount);
		setInitLoading(false);
	}, [products]);

	const fetchMoreData = useCallback(() => {
		setUserReturns(false);
		const prevOffset = offset;
		const pNum = (storeState?.page || 0) + 1;
		if (prevOffset + limit > totalCount!) {
			setOffset(prevOffset);
			setIsInfiniteLoading(false);
		} else {
			setOffset(pNum * limit);
			setIsInfiniteLoading(true);
		}
		setPage(pNum);
	}, [offset, totalCount]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && !isInfiniteLoading) {
					fetchMoreData();
				}
			},
			{ threshold: 0.1 }
		);

		const scrollWatcher = document.querySelector('#scroll-watch');
		if (scrollWatcher) observer.observe(scrollWatcher);

		return () => observer.disconnect();
	}, [isInfiniteLoading, fetchMoreData]);

	useEffect(() => {
		if (brandId) {
			getBrandById({ id: brandId });
			window.scrollTo(0, 0);
		}
	}, [brandId]);

	useEffect(() => {
		setIsBrandLoaded(!!((brandId && storeState?.brand) || !brandId));
	}, [brandId, storeState?.brand]);
	useGetUrlParameters(setSearchTerm, setSelectedFilters, hasMappedAllFilters, isBrandLoaded);
	useSetUrlParameters(setFilterUrl, searchTerm, selectedFilters);
	return (
		<>
			<ProductListComponent
				products={products}
				totalCount={totalCount || 0}
				viewMode={viewMode}
				initLoading={initLoading}
				infiniteLoading={isInfiniteLoading}
			/>
			<div id='scroll-watch' style={{ height: '0.5px' }}></div>
		</>
	);
};

export default ProductsPage;
